import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Star, MapPin, User } from 'lucide-react';
import axios from 'axios';

import { API_URL } from '../utils/api';

const FeaturedCarousel = ({ type = 'sellers' }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetchFeatured();
  }, []);

  const fetchFeatured = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/promotions/active`);
      if (type === 'sellers') {
        setItems(res.data.featured_sellers || []);
      } else {
        setItems(res.data.featured_tradies || []);
      }
    } catch (err) {
      console.error('Failed to fetch featured', err);
    } finally {
      setLoading(false);
    }
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex gap-4 overflow-hidden">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex-shrink-0 w-72 h-48 bg-stone-100 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return null; // Don't show section if no featured items
  }

  return (
    <div className="relative">
      {/* Scroll buttons */}
      {items.length > 3 && (
        <>
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-stone-50 transition-colors -ml-4"
          >
            <ChevronLeft className="h-5 w-5 text-stone-600" />
          </button>
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:bg-stone-50 transition-colors -mr-4"
          >
            <ChevronRight className="h-5 w-5 text-stone-600" />
          </button>
        </>
      )}

      {/* Carousel items */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide pb-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {items.map((item) => (
          <Link
            key={item.id}
            to={`/profile/${item.id}`}
            className="flex-shrink-0 w-72 bg-white rounded-xl border border-stone-200 overflow-hidden hover:shadow-lg transition-shadow group"
          >
            {/* Header with profile */}
            <div className="p-4 flex items-center gap-3 border-b border-stone-100">
              {item.profile_photo ? (
                <img
                  src={item.profile_photo}
                  alt={item.name}
                  className="w-14 h-14 rounded-full object-cover"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-teal-100 to-sky-200 flex items-center justify-center">
                  <User className="h-7 w-7 text-teal-600" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                  <span className="text-xs text-amber-600 font-medium">Featured</span>
                </div>
                <h3 className="font-semibold text-stone-900 truncate group-hover:text-teal-600 transition-colors">
                  {item.name}
                </h3>
                {item.location && (
                  <p className="text-xs text-stone-500 flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {item.location}
                  </p>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              {type === 'sellers' && item.sample_listing ? (
                <div className="flex gap-3">
                  {item.sample_listing.images?.[0] && (
                    <img
                      src={item.sample_listing.images[0]}
                      alt=""
                      className="w-20 h-20 rounded-lg object-cover"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-stone-600 line-clamp-2">
                      {item.sample_listing.title}
                    </p>
                    <p className="text-teal-600 font-semibold mt-1">
                      ${item.sample_listing.price}
                    </p>
                  </div>
                </div>
              ) : type === 'tradies' && item.trade_services ? (
                <div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {item.trade_services.slice(0, 3).map((service, i) => (
                      <span key={i} className="px-2 py-0.5 bg-teal-50 text-teal-600 text-xs rounded-full">
                        {service}
                      </span>
                    ))}
                  </div>
                  {item.bio ? (
                    <p className="text-sm text-stone-600 line-clamp-2">{item.bio}</p>
                  ) : (
                    <p className="text-sm text-stone-400 italic">View profile for services</p>
                  )}
                  {item.service_state && (
                    <p className="text-xs text-stone-500 mt-2 flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      Servicing {item.service_state}
                    </p>
                  )}
                </div>
              ) : item.bio ? (
                <p className="text-sm text-stone-600 line-clamp-3">{item.bio}</p>
              ) : (
                <p className="text-sm text-stone-400 italic">View profile for more info</p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default FeaturedCarousel;
